import "../LeftNav.css";

import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Chip,
  Divider,
  InputAdornment,
  ListItemText,
  ListSubheader,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import { useRef, useState } from "react";

const selectSXOverride = {
  borderRadius: "8px",
  border: "2px solid #E8FCFD0D",
};

const MIN_ITEMS_FOR_BULK_OPTIONS = 5;
const MIN_ITEMS_FOR_SEARCH = 15;

const byDisplayName = (a: PracticeMessage, b: PracticeMessage) =>
  a.displayName.localeCompare(b.displayName, undefined, {
    sensitivity: "base",
  });

interface GlobalFilterProps {
  selectValues: PracticeMessage[];
  values: PracticeMessage[];
  label: string;
  onChange: (event: SelectChangeEvent<string[]>) => void;
}

export function GlobalFilter({
  selectValues,
  values,
  label,
  onChange,
}: GlobalFilterProps) {
  const iconPath = `/static/images/${label.toLowerCase()}-icon.svg`;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectValuesIds = selectValues.map(
    (selectValue) => selectValue.wieldyId,
  );

  const showSearch = values.length > MIN_ITEMS_FOR_SEARCH;

  const handleOpen = () => {
    setPinnedIds(showSearch ? selectValuesIds : []);
    setSearch("");
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearch("");
  };

  const query = search.trim().toLowerCase();
  const visibleValues = values.filter((practice) =>
    practice.displayName.toLowerCase().includes(query),
  );
  const pinnedValues = visibleValues
    .filter((practice) => pinnedIds.includes(practice.wieldyId))
    .sort(byDisplayName);
  const unpinnedValues = visibleValues
    .filter((practice) => !pinnedIds.includes(practice.wieldyId))
    .sort(byDisplayName);

  const handleSelectAll = () => {
    const visibleIds = visibleValues.map((practice) => practice.wieldyId);
    const syntheticEvent = {
      target: {
        value: Array.from(new Set([...selectValuesIds, ...visibleIds])),
      },
    } as unknown as SelectChangeEvent<string[]>;
    onChange(syntheticEvent);
  };

  const handleUnselectAll = () => {
    const syntheticEvent = {
      target: { value: [] as string[] },
    } as unknown as SelectChangeEvent<string[]>;
    onChange(syntheticEvent);
  };

  const isAllSelected =
    visibleValues.length > 0 &&
    visibleValues.every((practice) =>
      selectValuesIds.includes(practice.wieldyId),
    );
  const isNoneSelected = selectValues.length === 0;
  const showBulkOptions = values.length > MIN_ITEMS_FOR_BULK_OPTIONS;

  const renderOption = (practice: PracticeMessage) => (
    <MenuItem key={practice.wieldyId} value={practice.wieldyId}>
      <Checkbox checked={selectValuesIds.includes(practice.wieldyId)} />
      <ListItemText primary={practice.displayName} />
    </MenuItem>
  );

  return (
    <div className="globalFilter">
      <div className="globalFilterLabel">
        <img src={iconPath} alt={`${label} Icon`} width="24" height="24" />
        <h3>{label}</h3>
      </div>
      <Select
        className="globalFilterSelect"
        IconComponent={KeyboardArrowDownOutlinedIcon}
        sx={{
          ".MuiSvgIcon-root": {
            fill: "#E9D7D9",
          },
          ".MuiOutlinedInput-notchedOutline": selectSXOverride,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": selectSXOverride,
          "&:hover .MuiOutlinedInput-notchedOutline": selectSXOverride,
          ".MuiSelect-icon": {
            left: 7,
          },
          ".globalFilterSelect": {
            borderRadius: "8px",
          },
          ".MuiSelect-select.MuiInputBase-input[role=combobox]": {
            paddingRight: "8px",
            paddingLeft: "32px",
            display: "flex",
            alignItems: "center",
          },
          ".MuiBox-root": {
            overflowX: "scroll",
            overflowY: "hidden",
            flexWrap: "nowrap",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          },
          ".MuiBox-root::-webkit-scrollbar": {
            width: 0,
            height: 0,
            background: "transparent",
          },
        }}
        labelId="multiple-practice-select"
        id="multiple-practice-select"
        inputProps={{
          MenuProps: {
            autoFocus: false,
            variant: "menu",
            TransitionProps: {
              onEntered: () => searchInputRef.current?.focus(),
            },
            MenuListProps: {
              sx: {
                backgroundColor: "#F9FAFB",
              },
            },
          },
        }}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple
        value={selectValuesIds}
        onChange={onChange}
        renderValue={(selected) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
            }}
          >
            {selected.map((value) => {
              const match = values.find(({ wieldyId }) => wieldyId === value);
              const selectLabel = match?.displayName;

              return (
                <Chip
                  sx={{
                    "&": {
                      height: "28px",
                      borderRadius: "4px",
                      color: "#E9D7D9",
                      backgroundColor: "#101828",
                      border: "solid 1px #25233F",
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#E9D7D9",
                    },
                    "& .MuiChip-label": {
                      fontSize: "0.875rem",
                    },
                  }}
                  size="small"
                  key={value}
                  label={selectLabel}
                  onDelete={onChange}
                  deleteIcon={<DisabledByDefaultRoundedIcon />}
                />
              );
            })}
          </Box>
        )}
      >
        {showSearch && (
          <ListSubheader sx={{ backgroundColor: "#F9FAFB", padding: "8px" }}>
            <TextField
              size="small"
              inputRef={searchInputRef}
              fullWidth
              placeholder="Search practices"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>
        )}
        {showBulkOptions && [
          <MenuItem
            key="bulk-options"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            onClick={(e) => e.preventDefault()}
          >
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                if (!isAllSelected) {
                  handleSelectAll();
                }
              }}
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: isAllSelected ? "#999" : "#1976d2",
                cursor: isAllSelected ? "default" : "pointer",
                "&:hover": {
                  textDecoration: isAllSelected ? "none" : "underline",
                },
              }}
            >
              Select All
            </Box>
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                if (!isNoneSelected) {
                  handleUnselectAll();
                }
              }}
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: isNoneSelected ? "#999" : "#666",
                cursor: isNoneSelected ? "default" : "pointer",
                "&:hover": {
                  color: isNoneSelected ? "#999" : "#d32f2f",
                  textDecoration: isNoneSelected ? "none" : "underline",
                },
              }}
            >
              Clear
            </Box>
          </MenuItem>,
          <Divider key="divider" />,
        ]}
        {visibleValues.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No practices found" />
          </MenuItem>
        )}
        {pinnedValues.map(renderOption)}
        {pinnedValues.length > 0 && unpinnedValues.length > 0 && <Divider />}
        {unpinnedValues.map(renderOption)}
      </Select>
    </div>
  );
}
